import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Lifebuoy, PaperPlaneTilt, BookOpen, DownloadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";

const fmt = (s) => new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export default function Support() {
  const [tickets, setTickets] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState("");

  const load = useCallback(() => {
    api.get("/support/tickets").then(({ data }) => setTickets(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/support/tickets", { subject, message });
      toast.success("Ticket created — our team will reply here");
      setSubject(""); setMessage(""); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const sendReply = async (tid) => {
    if (!reply.trim()) return;
    try {
      await api.post(`/support/tickets/${tid}/reply`, { message: reply });
      setReply(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl space-y-8" data-testid="support-page">
      <div className="bg-[#0A0D14] rounded-lg p-6 flex items-center gap-4" data-testid="support-manual-card">
        <span className="h-10 w-10 shrink-0 rounded-lg bg-[#0052FF]/20 flex items-center justify-center"><BookOpen size={20} weight="duotone" className="text-[#6ea0ff]" /></span>
        <div className="min-w-0">
          <h2 className="font-heading text-base font-extrabold tracking-tight text-white">User Manual</h2>
          <p className="text-xs text-[#aeb4c2]">Every module explained — purpose, features, how to use it, plus a recommended weekly routine. Share it with new team members.</p>
        </div>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/manual.pdf`} target="_blank" rel="noreferrer" data-testid="support-manual-download"
          className="ml-auto shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#0052FF] hover:bg-[#0043d1] text-white font-bold text-xs h-10 px-5 transition-colors">
          <DownloadSimple size={15} weight="bold" /> Download PDF
        </a>
      </div>

      <form onSubmit={create} className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-lg bg-[#FF6B35]/10 flex items-center justify-center"><Lifebuoy size={20} weight="duotone" className="text-[#FF6B35]" /></span>
          <div>
            <h2 className="font-heading text-lg font-extrabold tracking-tight text-[#0A0D14]">Need help?</h2>
            <p className="text-xs text-[#8a90a0]">Raise a ticket — the 5ads.ai team replies right here.</p>
          </div>
        </div>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} required placeholder="Subject (e.g., Payment not reflecting)"
          data-testid="support-subject-input" className="mt-5 h-11 rounded-lg" />
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} required placeholder="Describe the issue…"
          data-testid="support-message-input" rows={4}
          className="mt-3 w-full rounded-lg border border-[#E5E7EB] p-3 text-sm outline-none focus:border-[#FF6B35] resize-none" />
        <Button type="submit" disabled={busy} data-testid="support-create-btn"
          className="mt-4 rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold text-sm h-11 px-6">
          {busy ? "Creating…" : "Create ticket"}
        </Button>
      </form>

      <div className="space-y-3">
        <h3 className="font-heading text-sm font-extrabold text-[#0A0D14]">Your tickets</h3>
        {tickets === null ? (
          <p className="text-sm text-[#8a90a0]">Loading…</p>
        ) : tickets.length === 0 ? (
          <p className="text-sm text-[#8a90a0]" data-testid="support-empty">No tickets yet.</p>
        ) : tickets.map((t) => (
          <div key={t.id} className="bg-white border border-[#E5E7EB] rounded-lg" data-testid={`support-ticket-${t.id}`}>
            <button onClick={() => setOpen(open === t.id ? null : t.id)} className="w-full flex items-center gap-3 px-5 py-4 text-left">
              <span className="text-sm font-bold text-[#0A0D14]">{t.subject}</span>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${t.status === "open" ? "bg-success/10 text-[#00953e]" : "bg-[#F3F4F6] text-[#8a90a0]"}`}>{t.status}</span>
              {t.last_message_by === "admin" && <span className="text-[10px] font-bold text-[#FF6B35]">New reply</span>}
              <span className="ml-auto text-[11px] text-[#8a90a0]">{fmt(t.updated_at)}</span>
            </button>
            {open === t.id && (
              <div className="px-5 pb-5 border-t border-[#F3F4F6]">
                <div className="py-4 space-y-3 max-h-72 overflow-y-auto">
                  {t.messages.map((m, i) => (
                    <div key={i} className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${m.by === "admin" ? "bg-[#FF6B35]/10 text-[#7a3315]" : "ml-auto bg-[#F3F4F6] text-[#525866]"}`}>
                      <div className="text-[10px] font-bold text-[#8a90a0] mb-0.5">{m.by === "admin" ? "5ads.ai Support" : "You"} · {fmt(m.at)}</div>
                      {m.text}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply(t.id)}
                    placeholder="Write a reply…" data-testid="support-reply-input" className="h-10 rounded-lg" />
                  <Button onClick={() => sendReply(t.id)} data-testid="support-reply-send"
                    className="h-10 rounded-lg bg-[#0A0D14] hover:bg-[#1c2230] text-white font-bold text-xs px-4">
                    <PaperPlaneTilt size={14} weight="fill" className="mr-1" /> Send
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
